import React, { forwardRef, memo, useContext, useRef } from 'react'
import { useRouter } from 'next/router'
import { ScreenContext } from 'react-components'
import { HeaderSectionTransport } from '../../../transports/region-landing-page-transport'
import useSearchbarState from '../../../hooks/searchbar-state'
import ControlledSearchbar from '../controlled-searchbar'
import { BackgroundImages, Container, ContentWrapper, SubtitleWrapper, TitleWrapper } from './styles'
import { useScrollToSearch } from '../../../hooks/scroll-to-search'

type Navigation = HeaderSectionTransport['navigationSectionResponse']['navigation']

interface CommonHeroSectionProps {
    title: string
    subtitle: string
    backgroundImages: BackgroundImages
    logo?: Navigation['logo']
    textColor?: string
    hasOverlay?: boolean
    initialLocation?: string
}

const CommonHeroSection = forwardRef<HTMLDivElement, CommonHeroSectionProps>(
    ({ title, subtitle, backgroundImages, logo, textColor, hasOverlay, initialLocation }, ref) => {
        const { query } = useRouter()
        const { isDesktop, isTablet } = useContext(ScreenContext)
        const innerRef = useRef<HTMLDivElement | null>(null)

        const isSticky = useScrollToSearch(innerRef, isDesktop || isTablet)

        const setRefs = React.useCallback(
            (element: HTMLDivElement | null) => {
                innerRef.current = element
                if (typeof ref === 'function') {
                    ref(element)
                } else if (ref) {
                    ref.current = element
                }
            },
            [ref],
        )

        const {
            searchbar,
            locationValue,
            onLocationChange,
            onArrivalDateChange,
            onDepartureDateChange,
            onAdultsCountChange,
            onChildrenCountChange,
        } = useSearchbarState({ query, location: initialLocation })

        return (
            <Container backgroundImages={backgroundImages} hasOverlay={hasOverlay}>
                <ContentWrapper ref={setRefs}>
                    <TitleWrapper textColor={textColor}>{title}</TitleWrapper>
                    <SubtitleWrapper textColor={textColor}>{subtitle}</SubtitleWrapper>
                    <ControlledSearchbar
                        location={locationValue}
                        arrivalDate={searchbar.arrival}
                        departureDate={searchbar.departure}
                        adultsCount={searchbar.adults}
                        childrenCount={searchbar.kids}
                        onLocationChange={onLocationChange}
                        onArrivalDateChange={onArrivalDateChange}
                        onDepartureDateChange={onDepartureDateChange}
                        onAdultsCountChange={onAdultsCountChange}
                        onChildrenCountChange={onChildrenCountChange}
                        petsAllowed={false}
                        showMainSearchBar
                        isSticky={isSticky}
                        logo={logo}
                    />
                </ContentWrapper>
            </Container>
        )
    },
)

export default memo(CommonHeroSection)
